import { z } from 'zod';

// Structured error report — the wizard UI renders this and offers
// "fix in spreadsheet, re-upload" or "commit valid rows only".

export type ImportErrorKind =
  | 'header_missing'
  | 'header_unknown'
  | 'row_invalid'
  | 'duplicate_in_file'
  | 'duplicate_with_db'
  | 'foreign_key_missing'
  | 'value_out_of_range';

export interface ImportError {
  row: number; // 1-indexed; 0 means file-level
  column?: string | undefined;
  kind: ImportErrorKind;
  message: string;
  // Raw cell value involved, if any — useful for the wizard to highlight.
  value?: string | undefined;
}

export interface ImportSummary {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ImportError[];
  // Warnings don't block commit — e.g. trimming a trailing comma in a phone.
  warnings: ImportError[];
}

export function emptySummary(): ImportSummary {
  return { totalRows: 0, validRows: 0, invalidRows: 0, errors: [], warnings: [] };
}

export const importErrorSchema: z.ZodType<ImportError> = z.object({
  row: z.number().int().nonnegative(),
  column: z.string().optional(),
  kind: z.enum([
    'header_missing',
    'header_unknown',
    'row_invalid',
    'duplicate_in_file',
    'duplicate_with_db',
    'foreign_key_missing',
    'value_out_of_range',
  ]),
  message: z.string(),
  value: z.string().optional(),
});

export const importSummarySchema: z.ZodType<ImportSummary> = z.object({
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  invalidRows: z.number().int().nonnegative(),
  errors: z.array(importErrorSchema),
  warnings: z.array(importErrorSchema),
});
